// MIT License - Copyright (c) 2026 Doug McNall
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import './CommandPalette.css';

const CommandPalette = ({ tabs, onNavigate, onOpenCustomerDetail, onOpenOrderDetail, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Build nav results from tabs
  const navResults = query.length === 0 ? tabs.slice(0, 6).map(t => ({
    type: 'nav', id: t.id, label: t.label, subtitle: 'Navigate', tab: t
  })) : tabs
    .filter(t => t.label.toLowerCase().includes(query.toLowerCase()))
    .map(t => ({ type: 'nav', id: t.id, label: t.label, subtitle: 'Navigate', tab: t }));

  // Derive which entity types this user can search based on accessible tabs
  const accessibleTabIds = new Set(tabs.map(t => t.id));
  const canSearchProducts  = accessibleTabIds.has('products');
  const canSearchOrders    = accessibleTabIds.has('orders');
  const canSearchCustomers = accessibleTabIds.has('customers');

  // Search API on query change
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [productsRes, ordersRes, customersRes] = await Promise.allSettled([
          canSearchProducts  ? api.products.getAll()  : Promise.resolve(null),
          canSearchOrders    ? api.orders.getAll()    : Promise.resolve(null),
          canSearchCustomers ? api.customers.getAll() : Promise.resolve(null),
        ]);

        const q = query.toLowerCase();
        const items = [];

        if (productsRes.status === 'fulfilled') {
          const products = productsRes.value?.data || [];
          (Array.isArray(products) ? products : []).filter(p =>
            p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)
          ).slice(0, 3).forEach(p => items.push({
            type: 'product', id: `product-${p.id}`, label: p.name,
            subtitle: `Product · ${p.sku || ''}`, data: p
          }));
        }

        if (ordersRes.status === 'fulfilled') {
          const orders = ordersRes.value?.data || [];
          (Array.isArray(orders) ? orders : []).filter(o =>
            o.id?.toLowerCase().includes(q) || o.customer_name?.toLowerCase().includes(q)
          ).slice(0, 3).forEach(o => items.push({
            type: 'order', id: `order-${o.id}`, label: `Order ${o.id}`,
            subtitle: `Order · ${o.customer_name || ''} · ${o.status || ''}`, data: o
          }));
        }

        if (customersRes.status === 'fulfilled') {
          const customers = customersRes.value?.data || [];
          (Array.isArray(customers) ? customers : []).filter(c =>
            c.first_name?.toLowerCase().includes(q) ||
            c.last_name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q)
          ).slice(0, 3).forEach(c => items.push({
            type: 'customer', id: `customer-${c.id}`, label: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email,
            subtitle: `Customer · ${c.email || ''}`, data: c
          }));
        }

        setResults(items);
      } catch (e) {
        // silent fail
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(searchTimeout.current);
  }, [query]);

  const allItems = [...navResults, ...results];

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback((item) => {
    if (item.type === 'nav') {
      onNavigate(item.tab.id);
    } else if (item.type === 'customer') {
      onOpenCustomerDetail(item.data.id, `${item.data.first_name || ''} ${item.data.last_name || ''}`.trim());
    } else if (item.type === 'order') {
      onOpenOrderDetail(item.data.id, item.data.id);
    } else if (item.type === 'product') {
      onNavigate('products');
    }
    onClose();
  }, [onNavigate, onOpenCustomerDetail, onOpenOrderDetail, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (allItems[selectedIndex]) handleSelect(allItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const typeIcon = { nav: '→', product: '📦', order: '🛒', customer: '👤' };

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={e => e.stopPropagation()}>
        <div className="cp-input-row">
          <span className="cp-search-icon">⌘</span>
          <input
            ref={inputRef}
            className="cp-input"
            placeholder="Search pages, products, orders, customers..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {loading && <span className="cp-spinner">⟳</span>}
          <kbd className="cp-esc" onClick={onClose}>esc</kbd>
        </div>

        {allItems.length > 0 && (
          <div className="cp-results">
            {query.length === 0 && <div className="cp-section-label">Quick Navigation</div>}
            {query.length >= 2 && navResults.length > 0 && <div className="cp-section-label">Pages</div>}
            {navResults.map((item, i) => (
              <div
                key={item.id}
                className={`cp-item ${selectedIndex === i ? 'selected' : ''}`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(i)}
              >
                <span className="cp-item-icon">{typeIcon[item.type]}</span>
                <span className="cp-item-label">{item.label}</span>
                <span className="cp-item-subtitle">{item.subtitle}</span>
              </div>
            ))}

            {results.length > 0 && (
              <>
                <div className="cp-section-label">Results</div>
                {results.map((item, i) => {
                  const idx = navResults.length + i;
                  return (
                    <div
                      key={item.id}
                      className={`cp-item ${selectedIndex === idx ? 'selected' : ''}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                    >
                      <span className="cp-item-icon">{typeIcon[item.type]}</span>
                      <span className="cp-item-label">{item.label}</span>
                      <span className="cp-item-subtitle">{item.subtitle}</span>
                    </div>
                  );
                })}
              </>
            )}

            {query.length >= 2 && allItems.length === 0 && !loading && (
              <div className="cp-empty">No results for "{query}"</div>
            )}
          </div>
        )}

        <div className="cp-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
